import React, { useCallback, useEffect, useState } from 'react';
import { ProductImageSlide } from '@/types/common';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Pagination, Thumbs } from 'swiper';
import FreeMode from 'swiper';
import Image from 'next/image';
import styled from '@mui/system/styled';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTranslation from 'next-translate/useTranslation';
import { ChevronRight } from '@/components/icons';
import theme from '@/mui/theme';
import placeholderProduct from '@/public/placeholder-product-gray.svg';

export interface ProductDetailImageProps {
  height?: string;
  /**
   * Slide dello slider. La prima e' l'immagine principale, le successive sono le immagini
   * alternative (angle images) del prodotto. thumbnailImage e' opzionale: se assente
   * l'anteprima riusa backgroundImage.
   */
  slide: ProductImageSlide[];
  showDots?: boolean;
  showPreview?: boolean;
  /** Mostra le frecce di navigazione (solo con piu' di una slide). */
  showArrows?: boolean;
}

const StyledSwiper = styled(Swiper)({
  '&.hide-dots .pagination-container': {
    display: 'none'
  },
  '.swiper-pagination-bullet': {
    height: '4px',
    width: '4px',
    marginRight: '6px',
    borderRadius: '50%',
    backgroundColor: '#000000',
    opacity: '0.4',
    // i pallini sono <button>: azzero lo stile nativo e rendo visibile il focus da tastiera
    padding: '0',
    border: '0',
    appearance: 'none',
    cursor: 'pointer',
    '&:focus-visible': {
      outline: '2px solid #ff5c04',
      outlineOffset: '3px',
      opacity: '1',
    },
    '&:last-of-type': {
      marginRight: '0'
    },
    '&.swiper-pagination-bullet-active': {
      opacity: '1'
    }
  },
  '.swiper-pagination': {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    position: 'relative',
  },
  [theme.breakpoints.up('sm')]: {
    '.swiper-slide': {
      display: 'flex',
      alignItems: 'center',
      '> span': {
        position: 'absolute!important',
        width: '100%!important',
        height: '100%!important',
        top: 0,
        left: 0,
        zIndex: '-1',
      }
    },
    '.swiper-pagination': {
      height: '30px',
    },
    '.swiper-pagination-bullet': {
      height: '8px',
      width: '8px',
      marginRight: '10px',
    }
  },
  '&.swiper-container-thumbs': {
    [theme.breakpoints.up('sm')]: {
      marginTop: '6px',
    },
    marginTop: '9px',
    '.swiper-slide': {
      height: "80px",
      width: "80px!important",
      maxWidth: "80px",
      // la miniatura e' cliccabile: il puntatore deve dirlo anche sul bordo, fuori dal bottone
      cursor: 'pointer',
      '&.swiper-slide-thumb-active': {
        border: '3px solid #ff5c04',
        boxSizing: 'border-box',
      }
    }
  }
});

/**
 * Stile della singola freccia di navigazione.
 * E' l'intero bottone (44x44) a essere l'elemento di navigazione di Swiper, cosi' tutta l'area
 * cerchiata e' cliccabile e non solo l'icona.
 *
 * @param side lato su cui ancorare la freccia
 * @param dotsReserveSpace true se i pallini occupano spazio in fondo allo swiper (da sm in su):
 *                         in quel caso la freccia va ricentrata sull'immagine
 */
const arrowSx = (side: 'prev' | 'next', dotsReserveSpace: boolean) => ({
  position: 'absolute',
  [side === 'prev' ? 'left' : 'right']: { xs: '8px', sm: '12px' },
  // i pallini aggiungono 30px in fondo allo swiper (vedi '.swiper-pagination' qui sopra)
  top: dotsReserveSpace ? { xs: '50%', sm: 'calc(50% - 15px)' } : '50%',
  transform: 'translateY(-50%)',
  zIndex: 99,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '44px',
  height: '44px',
  padding: 0,
  border: 0,
  borderRadius: '50%',
  // le frecce stanno sopra la foto prodotto: senza sfondo risultano illeggibili
  backgroundColor: 'rgba(255, 255, 255, 0.85)',
  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
  color: '#000000',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease, opacity 0.2s ease',
  '&:hover': {
    backgroundColor: '#ffffff',
  },
  '&:focus-visible': {
    outline: '2px solid #ff5c04',
    outlineOffset: '2px',
  },
  // aria-disabled invece di disabled: il bottone resta raggiungibile da tastiera e non perde il
  // focus quando si arriva a inizio/fine (il click e' comunque inerte, vedi onPrevClick/onNextClick di Swiper)
  '&[aria-disabled="true"]': {
    opacity: 0.35,
    cursor: 'default',
    boxShadow: 'none',
  }
});

/**
 * Mette in sicurezza un testo da inserire in un attributo HTML.
 * Serve perche' renderBullet di Swiper lavora su stringhe di HTML e le etichette arrivano da HCL.
 *
 * @param value testo da inserire nell'attributo
 * @returns testo con i caratteri speciali sostituiti dalle entita'
 */
const escapeHtmlAttribute = (value: string) => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

/**
 * Stile del bottone che avvolge ogni miniatura: riempie la slide e non aggiunge decorazioni,
 * il bordo di selezione resta quello della slide (.swiper-slide-thumb-active).
 */
const thumbButtonSx = {
  display: 'block',
  position: 'relative',
  width: '100%',
  height: '100%',
  padding: 0,
  border: 0,
  background: 'none',
  cursor: 'pointer',
  // L'anello di focus e' un overlay e non un outline: verso l'esterno verrebbe ritagliato
  // dall'overflow hidden del contenitore, verso l'interno finirebbe sotto l'immagine.
  // Il filo bianco lo tiene leggibile sia sulle miniature chiare sia su quelle scure.
  '&:focus-visible': {
    outline: 'none',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      border: '3px solid #ff5c04',
      boxShadow: 'inset 0 0 0 2px #ffffff',
      pointerEvents: 'none',
    }
  }
};

/**
 * Swiper 6 non dichiara nei typing il flag `destroyed`, che pero' esiste a runtime
 * (lo usa anche swiper-react per non aggiornare istanze gia' distrutte).
 *
 * @param swiper istanza di Swiper
 * @returns true se l'istanza e' ancora utilizzabile
 */
const isAlive = (swiper: SwiperCore) => !(swiper as any).destroyed;

// install Swiper modules
SwiperCore.use([Navigation, Pagination, FreeMode, Thumbs]);

const ProductDetailImage: React.FC<ProductDetailImageProps> = ({ height = 'main', slide, showDots = true, showPreview = false, showArrows = false }) => {
  const { t } = useTranslation('common');
  const matches = useMediaQuery('(min-width:600px)');
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore>();
  const [mainSwiper, setMainSwiper] = useState<SwiperCore>();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  // le frecce hanno senso solo quando c'e' piu' di un'immagine da scorrere
  const withArrows = showArrows && slide?.length > 1;
  // solo lo swiper principale di PDP mostra i pallini "in flusso": nella modale sono nascosti da sm in su
  const dotsReserveSpace = showDots && !showPreview;

  // la posizione corrente e' tenuta in React (non letta dalle classi di Swiper) per poterla esporre
  // come aria-disabled sulle frecce e aria-current sulle miniature
  const syncState = useCallback((swiper: SwiperCore) => {
    setActiveIndex(swiper.activeIndex);
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  }, []);

  // Firma del set di immagini: cambia quando si passa a un altro prodotto.
  // Usata come key sugli swiper per **rimontarli** a ogni cambio prodotto. Serve perche' navigando
  // tra due PDP senza ricaricare la pagina (es. dai prodotti correlati) swiper-react non riallinea
  // l'istanza esistente: Swiper resta con il conteggio di slide precedente (pallini sbagliati, e
  // passando a un prodotto con piu' immagini lo slider non scorre) e la navigazione resta agganciata
  // ai nodi delle frecce che React ha nel frattempo distrutto, rendendole inerti.
  const slidesSignature = (slide || []).map((item) => item?.backgroundImage?.mediaItemUrl).join('|');

  // Reset dello stato React quando cambia il prodotto. Lo Swiper viene rimontato dalla key
  // (vedi il commento su slidesSignature), ma activeIndex/isBeginning/isEnd vivono qui.
  useEffect(() => {
    if (thumbsSwiper && isAlive(thumbsSwiper)) {
      thumbsSwiper.update();
    }
    if (!mainSwiper || !isAlive(mainSwiper)) {
      return;
    }
    mainSwiper.update();
    mainSwiper.slideTo(0, 0);
    syncState(mainSwiper);
  }, [slidesSignature, mainSwiper, thumbsSwiper, syncState]);

  const handleSwiper = (swiper: SwiperCore) => {
    setMainSwiper(swiper);
    syncState(swiper);
  };

  // swiper 6 tipizza onSlideChange con l'interfaccia delle props invece che con quella dell'istanza:
  // e' un difetto dei typing upstream, a runtime arriva comunque l'istanza
  const handleSlideChange = (swiper: any) => syncState(swiper as SwiperCore);

  // Frecce direzionali: funzionano con il focus su un qualsiasi elemento dello slider
  // (frecce, pallini, miniature), come ci si aspetta da un carosello.
  // Il listener sta sul contenitore e non sui componenti Swiper perche' swiper-react converte
  // ogni prop on[A-Z] in un listener di eventi Swiper: un onKeyDown non arriverebbe mai al DOM.
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!mainSwiper || !slide || slide.length <= 1) {
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      mainSwiper.slideNext();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      mainSwiper.slidePrev();
    }
  };

  // Attenzione: la prop va OMESSA quando non servono le frecce, non passata come undefined.
  // swiper-react tratta navigation come parametro noto e un undefined sovrascriverebbe il default
  // { nextEl: null, prevEl: null, ... }; Navigation.init legge params.navigation.nextEl senza
  // protezioni e andrebbe in TypeError su ogni prodotto con una sola immagine.
  const navigationProps = withArrows ? {
    navigation: {
      nextEl: '.swiper-navigation-next',
      prevEl: '.swiper-navigation-prev',
    }
  } : {};

  // i pallini sono <button> e non <div>: cosi' sono raggiungibili con TAB e attivabili con Invio/Spazio.
  // renderBullet vuole una stringa di HTML, quindi l'etichetta va messa in sicurezza a mano.
  const renderBullet = (index: number, className: string) => {
    const label = slide?.[index]?.backgroundImage?.altText || String(index + 1);
    return `<button type="button" class="${className}" aria-label="${escapeHtmlAttribute(label)}"></button>`;
  };

  const pagination = {
    el: '.swiper-pagination',
    type: 'bullets' as const,
    clickable: true,
    renderBullet,
  };

  const arrows = withArrows ? (
    <>
      <Box
        component="button"
        type="button"
        className="swiper-navigation-prev"
        aria-label={t('pagination-prev-label', {}, { default: 'Precedente' })}
        aria-disabled={isBeginning}
        sx={arrowSx('prev', dotsReserveSpace)}
      >
        <ChevronRight width="11px" height="18px" style={{ transform: 'rotate(180deg)' }} aria-hidden focusable="false" />
      </Box>
      <Box
        component="button"
        type="button"
        className="swiper-navigation-next"
        aria-label={t('pagination-next-label', {}, { default: 'Successiva' })}
        aria-disabled={isEnd}
        sx={arrowSx('next', dotsReserveSpace)}
      >
        <ChevronRight width="11px" height="18px" aria-hidden focusable="false" />
      </Box>
    </>
  ) : null;

  return (
    <Box onKeyDown={handleKeyDown}>
      {!showPreview &&
        <StyledSwiper
          key={`main-${slidesSignature}`}
          className={!showDots ? 'hide-dots' : ''}
          spaceBetween={0}
          slidesPerView={1}
          pagination={pagination}
          {...navigationProps}
          onSwiper={handleSwiper}
          onSlideChange={handleSlideChange}
        >

          {/* la key va anche sulla SwiperSlide: getChildren di swiper-react "srotola" i Fragment
              per raccogliere le slide, e la key del Fragment andrebbe persa */}
          {(slide.length > 0 && slide[0].backgroundImage.mediaItemUrl) ? slide.map((slideContent, index) => (
            <React.Fragment key={index}>
              {slideContent.backgroundImage ?
                <SwiperSlide key={index} style={{ height: matches ? slideContent.backgroundImage.mediaDetails.height : "auto" }}>
                  {matches &&
                    <Image
                      layout="responsive"
                      src={slideContent.backgroundImage.mediaItemUrl}
                      alt={slideContent.backgroundImage.altText}
                      width={slideContent.backgroundImage.mediaDetails.width}
                      height={slideContent.backgroundImage.mediaDetails.height}
                      objectFit="cover"
                    />
                  }
                  {!matches &&
                    <Image
                      layout="responsive"
                      src={slideContent.backgroundImage.mediaItemUrl}
                      alt={slideContent.backgroundImage.altText}
                      width={"350px"}
                      height={"350px"}
                      objectFit="cover"
                    />
                  }
                </SwiperSlide> :
                <SwiperSlide key={index} style={{ height: matches ? placeholderProduct.height : "auto", maxHeight: 636 }}>
                  {matches &&
                    <Image
                      layout="responsive"
                      src={placeholderProduct.src}
                      alt={""}
                      width={placeholderProduct.width}
                      height={placeholderProduct.height}
                      objectFit="cover"
                    />
                  }
                  {!matches &&
                    <Image
                      layout="responsive"
                      src={placeholderProduct.src}
                      alt={""}
                      width={"350px"}
                      height={"350px"}
                      objectFit="cover"
                    />
                  }
                </SwiperSlide>
              }
            </React.Fragment>
          )) :
            <SwiperSlide style={{ height: matches ? placeholderProduct.height : "auto", maxHeight: 636 }}>
              {matches &&
                <Image
                  layout="responsive"
                  src={placeholderProduct.src}
                  alt={""}
                  width={placeholderProduct.width}
                  height={placeholderProduct.height}
                  objectFit="cover"
                />
              }
              {!matches &&
                <Image
                  layout="responsive"
                  src={placeholderProduct.src}
                  alt={""}
                  width={"350px"}
                  height={"350px"}
                  objectFit="cover"
                />
              }
            </SwiperSlide>
          }
          {arrows}
          <Container className="pagination-container" sx={{ bottom: { sm: '52px', xs: '12px' }, position: { sm: 'relative', xs: 'absolute' } }}>
            <Grid container rowSpacing={0}>
              <Grid item xs={12}>
                <div className="swiper-pagination"></div>
              </Grid>
            </Grid>
          </Container>
        </StyledSwiper>
      }
      {showPreview && <>
        <StyledSwiper
          key={`main-${slidesSignature}`}
          className={matches ? 'hide-dots' : ''}
          spaceBetween={0}
          slidesPerView={1}
          pagination={pagination}
          thumbs={{ swiper: thumbsSwiper }}
          {...navigationProps}
          onSwiper={handleSwiper}
          onSlideChange={handleSlideChange}
        >
          {(slide.length > 0 && slide[0].backgroundImage.mediaItemUrl) ? slide.map((slideContent, index) => (
            <React.Fragment key={index}>
              {slideContent.backgroundImage ?
                <SwiperSlide key={index} style={{ height: matches ? slideContent.backgroundImage.mediaDetails.height : "auto"}}>
                  {matches &&
                    <Image
                      layout="responsive"
                      src={slideContent.backgroundImage.mediaItemUrl}
                      alt={slideContent.backgroundImage.altText}
                      width={slideContent.backgroundImage.mediaDetails.width}
                      height={slideContent.backgroundImage.mediaDetails.height}
                      objectFit="cover"
                    />
                  }
                  {!matches &&
                    <Image
                      layout="responsive"
                      src={slideContent.backgroundImage.mediaItemUrl}
                      alt={slideContent.backgroundImage.altText}
                      width={"350px"}
                      height={"350px"}
                      objectFit="cover"
                      objectPosition={height === 'small' ? "left" : "center"}
                    />
                  }
                </SwiperSlide> :
                <SwiperSlide key={index} style={{ height: matches ? placeholderProduct.height : "auto", maxHeight: 636 }}>
                  {matches &&
                    <Image
                      layout="responsive"
                      src={placeholderProduct.src}
                      alt={""}
                      width={placeholderProduct.width}
                      height={placeholderProduct.height}
                      objectFit="cover"
                    />
                  }
                  {!matches &&
                    <Image
                      layout="responsive"
                      src={placeholderProduct.src}
                      alt={""}
                      width={"350px"}
                      height={"350px"}
                      objectFit="cover"
                      objectPosition={height === 'small' ? "left" : "center"}
                    />
                  }
                </SwiperSlide>
              }
            </React.Fragment>
          )) :
            <SwiperSlide style={{ height: matches ? placeholderProduct.height : "auto" }}>
              {matches &&
                <Image
                  layout="responsive"
                  src={placeholderProduct.src}
                  alt={""}
                  width={placeholderProduct.width}
                  height={placeholderProduct.height}
                  objectFit="cover"
                />
              }
              {!matches &&
                <Image
                  layout="responsive"
                  src={placeholderProduct.src}
                  alt={""}
                  width={"350px"}
                  height={"350px"}
                  objectFit="cover"
                  objectPosition={height === 'small' ? "left" : "center"}
                />
              }
            </SwiperSlide>
          }
          {arrows}
          <Container className="pagination-container" sx={{ bottom: { sm: '52px', xs: '12px' }, position: { sm: 'relative', xs: 'absolute' } }}>
            <Grid container rowSpacing={0}>
              <Grid item xs={12}>
                <div className="swiper-pagination"></div>
              </Grid>
            </Grid>
          </Container>
        </StyledSwiper>
        <StyledSwiper
          key={`thumbs-${slidesSignature}`}
          className='hide-dots'
          spaceBetween={10}
          slidesPerView={6}
          onSwiper={setThumbsSwiper}
          watchSlidesProgress={true}
          freeMode={true}
        >
          {(slide.length > 0 && slide[0].backgroundImage.mediaItemUrl !== null) ? slide.map((slideContent, index) => {
            // se HCL non fornisce la miniatura dedicata (ANGLEIMAGES_THUMBNAIL) si riusa l'immagine full
            const previewImage = slideContent.thumbnailImage || slideContent.backgroundImage;
            return (
            <React.Fragment key={index}>
              {slideContent.backgroundImage ?
                <SwiperSlide key={index}>
                  {/* la miniatura e' un <button>: Swiper sposta lo slide principale sull'evento "tap",
                      che da tastiera non viene mai emesso, quindi il click chiama slideTo direttamente */}
                  <Box
                    component="button"
                    type="button"
                    onClick={() => mainSwiper?.slideTo(index)}
                    aria-label={previewImage.altText || String(index + 1)}
                    aria-current={activeIndex === index}
                    sx={thumbButtonSx}
                  >
                    {matches &&
                      <Image
                        layout="fill"
                        src={previewImage.mediaItemUrl}
                        alt=""
                        objectFit="cover"
                      />
                    }
                    {!matches &&
                      <Image
                        layout="responsive"
                        src={previewImage.mediaItemUrl}
                        alt=""
                        width={"350px"}
                        height={"350px"}
                        objectFit="cover"
                        objectPosition={height === 'small' ? "left" : "center"}
                      />
                    }
                  </Box>
                </SwiperSlide> :
                <SwiperSlide key={index}>
                  {matches &&
                    <Image
                      layout="fill"
                      src={placeholderProduct.src}
                      alt={""}
                      objectFit="cover"
                    />
                  }
                  {!matches &&
                    <Image
                      layout="responsive"
                      src={placeholderProduct.src}
                      alt={""}
                      width={"350px"}
                      height={"350px"}
                      objectFit="cover"
                      objectPosition={height === 'small' ? "left" : "center"}
                    />
                  }
                </SwiperSlide>
              }
            </React.Fragment>
          )}) :
            <SwiperSlide>
              {matches &&
                <Image
                  layout="fill"
                  src={placeholderProduct.src}
                  alt={""}
                  objectFit="cover"
                />
              }
              {!matches &&
                <Image
                  layout="responsive"
                  src={placeholderProduct.src}
                  alt={""}
                  width={"350px"}
                  height={"350px"}
                  objectFit="cover"
                  objectPosition={height === 'small' ? "left" : "center"}
                />
              }
            </SwiperSlide>
          }
        </StyledSwiper>
      </>}
    </Box>
  )
}

export default ProductDetailImage;
